import { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToasterContext";
import { getFilteredBussinessIds } from "../helpers/eventListingFiltersHelpers";
import { createFilterThunk, getEventFilterThunk } from "../store/events/eventThunk";
import { iFilterItem } from "../types/filterTypes";
import { useAppDispatch, useAppSelector } from "./storeHooks";

interface iUseSavedFiltersParams {
	gridId?: string;
	gridType: string;
	/** A link's filters; when present they win over the saved ones. */
	linkedItems?: iFilterItem[];
	setItems: (items: iFilterItem[]) => void;
	/** Event lists also send the CMS ids behind the chosen values (the API matches new events against them). */
	withCmsMeta?: boolean;
}

const NONE: iFilterItem[] = [];

const keyOf = (items: iFilterItem[]) =>
	JSON.stringify(
		items
			.map(({ field, operator, value }) =>
				JSON.stringify([
					field,
					operator,
					Array.isArray(value) ? value.map(String).sort() : value === undefined || value === null ? "" : String(value),
				]),
			)
			.sort(),
	);

const useSavedFilters = ({ gridId, gridType, linkedItems = NONE, setItems, withCmsMeta }: iUseSavedFiltersParams) => {
	const dispatch = useAppDispatch();
	const { showToast } = useToast();
	const [saving, setSaving] = useState(false);
	const saved = useAppSelector((state) => state.events.filters);
	const cms = useAppSelector((state) => state.events.cms);
	const setItemsRef = useRef(setItems);
	setItemsRef.current = setItems;
	const linkedKey = JSON.stringify(linkedItems);
	const storedKey = keyOf(gridId && saved?.gridId === gridId ? (saved.filters ?? []) : []);
	const [lastSave, setLastSave] = useState<{ stored: string; key: string } | null>(null);
	const savedKey = lastSave?.stored === storedKey ? lastSave.key : storedKey;

	useEffect(() => {
		if (gridId) dispatch(getEventFilterThunk({ gridId }));
	}, [gridId]);

	useEffect(() => {
		if (linkedItems.length) setItemsRef.current(linkedItems);
		else if (gridId && saved?.gridId === gridId) setItemsRef.current(saved.filters ?? []);
	}, [saved, gridId, linkedKey]);

	const save = (items: iFilterItem[]) => {
		if (!gridId || saving) return;
		setSaving(true);
		dispatch(
			createFilterThunk({
				filters: items,
				gridId,
				gridType,
				...(withCmsMeta && { meta: getFilteredBussinessIds(cms, items) }),
			}),
		)
			.unwrap()
			.then(() => {
				setLastSave({ stored: storedKey, key: keyOf(items) });
				showToast(items.length ? "Filter saved." : "Filter cleared — the list opens unfiltered.", undefined, "success");
			})
			.catch((error) => showToast(error?.message || "Couldn't save the filter. Please try again.", undefined, "error"))
			.finally(() => setSaving(false));
	};

	const canSave = (items: iFilterItem[]) => keyOf(items) !== savedKey;

	const clearSaved = () => {
		if (canSave(NONE)) save(NONE);
	};

	return { save, saving, canSave, clearSaved };
};

export default useSavedFilters;
